<template>
    <page :tabs="tabs">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :pinnedBottomRowData="pinnedBottomRowData"
            gridTheme="ag-theme-alpine-report"
            :isAutoWidth="true"
            :getRowStyle="rowStyle"
            :isSearch="false"
            :buttonToolbarClass="[
                'align-items-start flex-md-nowrap',
            ]"
            :defaultSort="sort"
            :defaultDesc="true"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <export-to-excel-button :href="excelHref" />
            <div
                class="
                    merge-source-wrapper
                    mb-0
                    subscribers-wrapper
                    d-none d-lg-flex
                    mr-1
                "
            >
                <form-input
                    id="source"
                    @input="(value) => searchSubscribers(value)"
                    :value="subscriberSearch"
                    placeholder="Search by subscriber"
                    autocomplete="off"
                    class="w-100"
                />
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-if="!!subscriberSearch && busy"
                >
                    <div
                        class="
                            guest-item
                            p-2
                            d-flex
                            align-items-center
                            justify-content-center
                        "
                    >
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="subscribersList.length && subscriberSearch"
                >
                    <div
                        class="guest-item p-2"
                        :key="subscriber.id"
                        v-for="subscriber in subscribersList"
                        @click="
                            selectSubscriber(subscriber.text, subscriber.value)
                        "
                    >
                        <span>{{ subscriber.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="
                        !subscribersList.length &&
                        !!subscriberSearch &&
                        !selectedSubscriber
                    "
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
                <div v-else />
            </div>
            <form-input
                class="mb-0 mr-1 d-none d-lg-flex"
                placeholder="Search by phone"
                v-model="phoneNumber"
                @input="onSearchInput"
            />
            <b-button-group class="d-lg-none d-block">
                <more-filters-dropdown>
                        <div
                            class="
                                merge-source-wrapper
                                mb-0
                                subscribers-wrapper
                                d-flex d-lg-none
                                mr-1
                            "
                        >
                            <form-input
                                id="source"
                                @input="(value) => searchSubscribers(value)"
                                :value="subscriberSearch"
                                placeholder="Search by subscriber"
                                autocomplete="off"
                                class="w-100"
                            />
                            <div
                                class="
                                    merge-guests-wrapper
                                    subscribers-list-wrapper
                                "
                                v-if="!!subscriberSearch && busy"
                            >
                                <div
                                    class="
                                        guest-item
                                        p-2
                                        d-flex
                                        align-items-center
                                        justify-content-center
                                    "
                                >
                                    <b-spinner
                                        variant="primary"
                                        :small="true"
                                    />
                                </div>
                            </div>
                            <div
                                class="
                                    merge-guests-wrapper
                                    subscribers-list-wrapper
                                "
                                v-else-if="
                                    subscribersList.length && subscriberSearch
                                "
                            >
                                <div
                                    class="guest-item p-2"
                                    :key="subscriber.id"
                                    v-for="subscriber in subscribersList"
                                    @click="
                                        selectSubscriber(
                                            subscriber.text,
                                            subscriber.value
                                        )
                                    "
                                >
                                    <span>{{ subscriber.text }}</span>
                                </div>
                            </div>
                            <div
                                class="
                                    merge-guests-wrapper
                                    subscribers-list-wrapper
                                "
                                v-else-if="
                                    !subscribersList.length &&
                                    !!subscriberSearch &&
                                    !selectedSubscriber
                                "
                            >
                                <div class="p-2">
                                    <span class="text-center"
                                        >Nothing found</span
                                    >
                                </div>
                            </div>
                            <div v-else />
                        </div>

                        <form-input
                            class="
                                mb-0
                                mr-1
                                subscribers-wrapper
                                d-block d-lg-none
                            "
                            placeholder="Search by phone"
                            v-model="phoneNumber"
                            @input="onSearchInput"
                        />
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
</template>

<script>
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import API from '../../../api';
import { ADMIN_SMS_LOG_REPORT_ENDPOINT } from '../../../endpoints';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import { toAmountDisplay } from '../../../utils/numbers';
import { truncate } from '../../../utils/strings';
import FormInput from '../../generic/form-input/form-input.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';

export default {
    components: {
        page,
        Grid,
        DateRangePicker,
        FormInput,
        SearchMultiselect,
        ExportToExcelButton,
        MoreFiltersDropdown,
    },
    name: 'AdminSmsLogReport',
    data() {
        return {
            busy: false,
            tabs: [
                {
                    title: 'SMS Log Report',
                    active: true,
                },
            ],
            dateRange: nameToRange(THIS_MONTH),
            dateRequestFormat: 'yyyy-MM-dd',
            phoneNumber: '',
            predefinedRanges: [
                LAST_MONTH,
                LAST_WEEK,
                LAST_YEAR,
                THIS_MONTH,
                THIS_WEEK,
                THIS_YEAR,
                TODAY,
                YESTERDAY,
            ],
            rowStyle: null,
            subscribersList: [],
            selectedSubscriber: '',
            subscriberSearch: '',
            totalAmount: null,
            totalCount: null,
            offset: 0,
            limit: 20,
            sort: 'created',
            desc: true,
        };
    },
    computed: {
        ...mapGetters('header', [
            'userUnicodeDateFormat',
            'userCurrencySymbol',
        ]),

        columnDefs() {
            return [
                {
                    field: 'created',
                    headerName: 'Timestamp',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'subscriber.id',
                    headerName: 'Subscriber ID',
                    minWidth: 120,
                },
                {
                    field: 'subscriber.email',
                    headerName: 'Subscriber Email',
                    minWidth: 200,
                },
                {
                    field: 'phone',
                    headerName: 'Phone',
                    sortable: false,
                    minWidth: 140,
                },
                {
                    field: 'provider',
                    headerName: 'Provider',
                    minWidth: 80,
                },
                {
                    field: 'is_custom',
                    headerName: 'Gateway',
                    minWidth: 90,
                    cellRenderer: ({ data }) => data.is_custom ? 'Subscriber' : 'Lodgix',                    
                },                       
                {
                    field: 'message_count',
                    headerName: 'SMS Count',
                    minWidth: 60,
                },
                {
                    field: 'amount',
                    headerName: 'SMS Price',
                    valueFormatter: ({ value, node }) => {
                        return toAmountDisplay({
                            amount: value,
                            currency: this.userCurrencySymbol,
                            decimalPlaces: node.rowPinned ? 2 : 8,
                        });
                    },
                    pdfExportOptions: {
                        valueFormatter: ({ value }) => {
                            return toAmountDisplay({
                                amount: value,
                                currency: this.userCurrencySymbol,
                                decimalPlaces: 8,
                            });
                        },
                    },
                },
                {
                    field: 'total',
                    sortable: false,
                    headerName: 'Total',
                    valueFormatter: ({ value, node }) => {
                        return toAmountDisplay({
                            amount: value,
                            currency: this.userCurrencySymbol,
                            decimalPlaces: node.rowPinned ? 2 : 8,
                        });
                    },
                    pdfExportOptions: {
                        valueFormatter: ({ value }) => {
                            return toAmountDisplay({
                                amount: value,
                                currency: this.userCurrencySymbol,
                                decimalPlaces: 8,
                            });
                        },
                    },
                },
                {
                    field: 'text',
                    sortable: false,
                    headerName: 'Text',
                    pdfExportOptions: {
                        valueFormatter: ({ value }) => {
                            return truncate(value, 50);
                        },
                    },
                    autoWidthExclude: true
                },
                {
                    field: 'error_code',
                    headerName: 'Error Code',
                    cellRenderer: ({ data }) => {
                        if (data.provider == 'TWILIO' && data.error_code > 0) {
                            return `<a target="_blank" href="https://www.twilio.com/docs/api/errors/${data.error_code}">${data.error_code}</a>`;
                        }
                        return 'N/A';
                    },                    
                },                
                {
                    field: 'status',
                    headerName: 'Status',
                },
            ];
        },
        excelHref() {
            return `${ADMIN_SMS_LOG_REPORT_ENDPOINT()}excel/?offset=${
                this.offset
            }&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&search=${
                this.search
            }&start_date=${this.startDateRequest}&end_date=${
                this.endDateRequest
            }&phone_number=${this.phoneNumber}&subscriber=${
                this.selectedSubscriber
            }`;
        },
        pinnedBottomRowData() {
            return [
                {
                    message_count: this.totalCount,
                    total: this.totalAmount,
                },
            ];
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(`${ADMIN_SMS_LOG_REPORT_ENDPOINT()}grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        phone_number: this.phoneNumber,
                        subscriber: this.selectedSubscriber,
                    },
                });
            };
        },
    },
    mounted() {
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        Promise.all([this.fetchSubscribers(), this.fetchTotals()]);
    },
    methods: {
        onSearchInput: debounce(function () {
            this.refresh();
        }, 500),
        async fetchTotals() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    `${ADMIN_SMS_LOG_REPORT_ENDPOINT()}totals/`,
                    {
                        params: {
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            phone_number: this.phoneNumber,
                            subscriber: this.selectedSubscriber,
                        },
                    }
                );
                this.totalAmount = data.total_amount;
                this.totalCount = data.total_count;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            } finally {
                this.busy = false;
            }
        },
        async fetchSubscribers() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    `${ADMIN_SMS_LOG_REPORT_ENDPOINT()}subscribers/`,
                    {
                        params: {
                            search: this.subscriberSearch,
                        },
                    }
                );
                this.subscribersList = data.map(({ user }) => ({
                    text: `${user.id} - ${user.email}`,
                    value: user.id,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not fetch subscribers' });
            } finally {
                this.busy = false;
            }
        },
        selectSubscriber(text, value) {
            this.subscriberSearch = text;
            this.selectedSubscriber = value;
            this.subscribersList = [];
            this.refresh();
        },
        searchSubscribers: debounce(function (value) {
            this.subscriberSearch = value;
            this.selectedSubscriber = '';
            if (value === '') {
                this.refresh();
            } else {
                this.fetchSubscribers();
            }
        }, 500),
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.fetchTotals();
            this.$refs.grid.refreshDataSource();
        },
    },
};
</script>

<style lang="scss" scoped>
.subscribers-list-wrapper {
    top: 40px;
}
.subscribers-wrapper {
    min-width: 300px;
}
</style>